import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createRoute,
  getAllActiveRoutes,
  getAllRoutes,
  getRouteByID,
  updateRoute,
} from "./services/route.services";
import { clearRouteDetails } from "./routeSlice";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { getAllActiveSectors } from "../sector/services/sector.services";
import { ISector } from "../sector/sectorModel";
import { closed } from "../../components/drawer/drawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import { STATUSARRAY } from "../../_contstants/common";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import IconButton from "../../components/common/IconButton";

const CreateRoute: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const SectorList = useAppSelector((state) => state.sector.SectorList);
  const RouteDetail = useAppSelector((state) => state.route.RouteDetail);
  const RouteID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.route.formLoading);
  const isIndirect = useAppSelector((state) => state.drawer.isIndirect);

  useEffect(() => {
    if (RouteID) {
      dispatch(getRouteByID(RouteID));
    }
  }, [RouteID]);

  useEffect(() => {
    dispatch(getAllActiveSectors());

    return () => {
      dispatch(clearRouteDetails());
      dispatch(closed());
    };
  }, []);

  const handleSubmit = async (values: any) => {
    try {
      if (!RouteID) {
        const insertPayload = {
          SectorID: values.SectorID,
          RouteName: values.RouteName,
          Description: values.Description,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createRoute(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearRouteDetails());

          if (isIndirect) {
            dispatch(getAllActiveRoutes());
          } else {
            dispatch(getAllRoutes());
          }
        }
      } else {
        const editPayload = {
          ID: RouteID,
          SectorID: values.SectorID,
          RouteName: values.RouteName,
          Description: values.Description,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateRoute(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearRouteDetails());
          dispatch(getAllRoutes());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={RouteDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem className="mui-drawer-header">
              <Typography.h4 className="m-0">
                {RouteID ? "Update Route" : "Add Route"}
              </Typography.h4>
              <IconButton
                type="button"
                fillMode={"flat"}
                themeColor={"dark"}
                style={{ marginRight: 4 }}
                onClick={() => dispatch(closed())}
                icon={RxCross1}
              />
            </GridLayoutItem>

            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="RouteName"
                label="Route"
                placeholder="Route"
                component={FormTextField}
                validator={requiredValidator}
                astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                id="SectorID"
                name="SectorID"
                label="Sector"
                astrike={true}
                placeholder="Sector"
                component={FormSelectionField}
                validator={requiredValidator}
                options={SectorList?.map((sector: ISector) => {
                  return {
                    value: sector?.ID,
                    label: sector?.SectorName,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="Description"
                label="Description"
                astrike={true}
                component={FormRichTextField}
                validator={requiredValidator}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                id={"IsActive"}
                name={"IsActive"}
                label={"Status"}
                component={FormSelectionField}
                validator={requiredValidator}
                astrike={true}
                placeholder="i.e. Active"
                options={STATUSARRAY?.map((status: any) => {
                  return {
                    value: status.value,
                    label: status.label,
                  };
                })}
              />
            </GridLayoutItem>

            <GridLayoutItem
              colSpan={3}
              style={{
                textAlign: "end",
                marginTop: "20px",
                width: "100%",
              }}
            >
              <ButtonWithLoading
                label={!RouteID ? "Save" : "Update"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />

              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginLeft: 4 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateRoute;
